import { useRef } from 'react';

import ReactToPrint from 'react-to-print';
import { FcPrint } from 'react-icons/fc';
import { QRCodeCanvas } from 'qrcode.react';
import styled from 'styled-components';

import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { WarehouseDeliveryFragmentFragment } from 'data/__generated__';
import dayjs from 'dayjs';

export type BoxToPrint = {
    bundleNumber: number;
    hasOnsiteSetup: boolean;
    isFreefood: boolean;
    roundColor: string;
    roundName: string;
    label: string;
};

const getLabel = (warehouseDelivery: WarehouseDeliveryFragmentFragment) => {
    return warehouseDelivery.site.name;
};

const getSubLabel = (warehouseDelivery: WarehouseDeliveryFragmentFragment) => {
    return `${dayjs(warehouseDelivery.deliveryDate).format('DD/MM/YYYY').slice(0, 5)} • Palette • #1/1`;
};

export const PrintWarehouseDeliveriesTags = ({
    warehouseDeliveries,
}: {
    warehouseDeliveries: WarehouseDeliveryFragmentFragment[];
}) => {
    const componentRef = useRef(null);

    return (
        <Container>
            <ReactToPrint
                trigger={() => {
                    // NOTE: could just as easily return <SomeComponent />. Do NOT pass an `onClick` prop
                    // to the root node of the returned component as it will be overwritten.
                    return (
                        <span>
                            <TotemPrimaryButton disabled={!warehouseDeliveries.length}>
                                <PrintButtonContent>
                                    <FcPrint size={50} />
                                    <PrintLabel>Imprimer les étiquettes</PrintLabel>
                                </PrintButtonContent>
                            </TotemPrimaryButton>
                        </span>
                    );
                }}
                content={() => componentRef.current}
            />
            <HiddenComponent>
                <Printable ref={componentRef}>
                    <WarehouseDeliveryTags>
                        {warehouseDeliveries.map((warehouseDelivery, index) => {
                            const { deliveryCode, roundColor, roundName } = warehouseDelivery;
                            const label = getLabel(warehouseDelivery);

                            return (
                                <WarehouseDeliveryTagContainer key={index} $hasBackground={false}>
                                    <TagContent>
                                        <Label $isTextLong={label.length > 23}>{label}</Label>
                                        <SubLabel>{getSubLabel(warehouseDelivery)}</SubLabel>
                                    </TagContent>
                                    <TagFooter>
                                        <CodeContainer>
                                            <QRCodeAndCode>
                                                <QRCodeCanvas value={deliveryCode} size={200} />
                                                <DeliveryCodeText>{deliveryCode}</DeliveryCodeText>
                                            </QRCodeAndCode>
                                            <Round $roundColor={roundColor}>{roundName || 'R__'}</Round>
                                        </CodeContainer>
                                    </TagFooter>
                                </WarehouseDeliveryTagContainer>
                            );
                        })}
                    </WarehouseDeliveryTags>
                </Printable>
            </HiddenComponent>
        </Container>
    );
};

const Container = styled.div`
    color: black;
`;

const PrintButtonContent = styled.div`
    display: flex;
    align-items: center;
`;

const PrintLabel = styled.span`
    margin-left: 15px;
`;

const HiddenComponent = styled.div`
    display: none;
`;

const Printable = styled.div`
    @page {
        size: landscape;
        margin: 12mm 15mm;
    }
`;

const WarehouseDeliveryTags = styled.div`
    display: table;
    width: 100%;
`;

const WarehouseDeliveryTagContainer = styled.div<{ $hasBackground: boolean }>`
    position: relative;
    display: inline-flex;
    flex-direction: column;
    align-items: space-between;
    width: 33%;
    padding: 30px 10px;
    border: 1px solid black;
    min-height: 320px;
    break-inside: avoid;
    background-color: ${({ $hasBackground }) => ($hasBackground ? 'lightblue' : 'transparent')};
`;

const Round = styled.div<{ $roundColor: string | null | undefined }>`
    display: flex;
    align-items: center;
    font-size: 30px;
    font-weight: 800;
    border-radius: ${({ theme }) => theme.borderRadius};
    background-color: ${({ $roundColor }) => $roundColor || 'black'};
    color: white;
    padding: 5px 10px;
`;

const TagContent = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    flex: 1;
    margin-top: 5px;
`;

const Label = styled.span<{ $isTextLong: boolean }>`
    font-size: ${({ $isTextLong }) => ($isTextLong ? 30 : 44)}px;
    background-color: 'transparent';
    font-weight: 800;
    color: black;
    text-align: center;
    width: 100%;
    height: 120px;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 10px;
    box-sizing: border-box;
    line-height: 1.2;
`;

const SubLabel = styled.span`
    font-size: 24px;
    color: black;
    text-align: center;
`;

const CodeContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 10px;
    gap: 20px;
`;

const QRCodeAndCode = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
`;

const DeliveryCodeText = styled.span`
    font-size: 11px;
    color: black;
    font-family: monospace;
    letter-spacing: 0.5px;
`;

const TagFooter = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 5px;
`;
